import React, { useEffect, useState } from 'react';

/**
 * Banner fijo arriba de la pantalla que aparece cuando el dispositivo
 * pierde conexión a internet. Pensado para terminales en campo.
 *
 * - Detecta `navigator.onLine` y los eventos `online` / `offline`
 * - Cuando recupera conexión muestra brevemente un banner verde "Conexión restablecida"
 *   y luego se oculta automáticamente
 */
export function ConexionBanner() {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );
  const [mostrarRestaurado, setMostrarRestaurado] = useState(false);

  useEffect(() => {
    function onOffline() {
      setOnline(false);
      setMostrarRestaurado(false);
    }
    function onOnline() {
      setOnline(true);
      setMostrarRestaurado(true);
      window.setTimeout(() => setMostrarRestaurado(false), 3500);
    }
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  if (online && !mostrarRestaurado) return null;

  const offlineStyle: React.CSSProperties = {
    position: 'sticky', top: 0, zIndex: 100,
    background: '#7f1d1d', color: '#fecaca',
    padding: '10px 14px',
    fontSize: '14px', fontWeight: 700,
    textAlign: 'center',
    borderBottom: '2px solid #ef4444',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  };
  const onlineStyle: React.CSSProperties = {
    ...offlineStyle,
    background: '#14532d', color: '#bbf7d0', borderBottom: '2px solid #22c55e',
  };

  if (!online) {
    return (
      <div style={offlineStyle} role="status" aria-live="polite">
        <span style={{ fontSize: '18px' }}>📡</span>
        Sin conexión a internet — los cambios se guardarán cuando vuelva la señal
      </div>
    );
  }

  return (
    <div style={onlineStyle} role="status" aria-live="polite">
      <span style={{ fontSize: '18px' }}>✓</span>
      Conexión restablecida
    </div>
  );
}
